import api from "./api";

// ─────────────────────────────────────────────────────────────────────────────
// Browser crash reporting → POST /api/client-errors → admin "Error Log".
//
// Captures uncaught errors, non-Axios unhandled promise rejections and React
// render crashes (via ErrorBoundary). API failures are NOT reported from here:
// the backend already logs every 5xx with full request context.
//
// Guard rails: identical errors are sent once per 30s, at most 20 reports per
// page load, and reporting failures are swallowed so this can never loop.
// ─────────────────────────────────────────────────────────────────────────────
const MAX_REPORTS = 20;
const DEDUPE_MS = 30_000;
const IGNORED = [
  /ResizeObserver loop/i,           // benign browser noise
  /^Script error\.?$/i,             // cross-origin script, no details available
  /chrome-extension:|moz-extension:/i,
];

let sent = 0;
const recent = new Map();

export function reportClientError({ kind = "error", message, stack, componentStack }) {
  try {
    const text = String(message || "Unknown error").slice(0, 2000);
    if (IGNORED.some((re) => re.test(text) || (stack && re.test(stack)))) return;
    if (sent >= MAX_REPORTS) return;
    const key = `${kind}|${text}`;
    const now = Date.now();
    if (recent.has(key) && now - recent.get(key) < DEDUPE_MS) return;
    recent.set(key, now);
    sent += 1;
    api.post("/client-errors", {
      kind,
      message: text,
      stack: stack ? String(stack).slice(0, 8000) : undefined,
      component_stack: componentStack ? String(componentStack).slice(0, 4000) : undefined,
      url: window.location.href,
    }).catch(() => {});
  } catch {
    /* reporting must never throw */
  }
}

const isAxiosError = (reason) =>
  reason &&
  (reason.isAxiosError === true ||
    reason.name === "AxiosError" ||
    (typeof reason === "object" && (reason.response || reason.request)));

export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    // Resource load failures (img/script 404) don't carry an Error object.
    if (!event.error && !event.message) return;
    reportClientError({
      kind: "error",
      message: event.error?.message || event.message,
      stack: event.error?.stack,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (isAxiosError(reason)) return;
    reportClientError({
      kind: "unhandledrejection",
      message: reason?.message || String(reason),
      stack: reason?.stack,
    });
  });
}
